/**
 * <s-newsletter-popup> — opens the email signup dialog it wraps after a delay.
 *
 * - Waits `delay` seconds (default 8) before opening, once per visitor.
 * - Closing the popup hides it for `snooze-days` (default 14).
 * - A successful signup hides it for good.
 * - After the signup form posts back (success or error), it reopens right away
 *   so the visitor sees the result message.
 * - Never auto-opens inside the theme editor, so it doesn't interrupt editing.
 *
 * Attributes:
 *   delay        — seconds before opening
 *   snooze-days  — days to stay hidden after a close
 */
const STORAGE_KEY = 'jeburk-newsletter-popup';

class SNewsletterPopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const hasResult = Boolean(dialog.querySelector('[data-signup-result]'));
    const succeeded = Boolean(dialog.querySelector('[data-signup-result="success"]'));

    dialog.addEventListener('close', () => {
      this.#remember(succeeded ? 'subscribed' : 'dismissed');
    }, opts);

    if (hasResult) {
      if (succeeded) {
        this.#remember('subscribed');
        const reward = dialog.querySelector('[data-signup-reward]');
        if (reward) reward.hidden = false;
      }
      this.#open(dialog);
      return;
    }

    if (window.Shopify?.designMode) return;
    if (!this.#shouldShow()) return;

    const delaySeconds = Number(this.getAttribute('delay') ?? 8);
    this.#timer = window.setTimeout(() => this.#open(dialog), Math.max(0, delaySeconds) * 1000);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #open(dialog) {
    if (dialog.open || document.querySelector('dialog:modal')) return;
    dialog.showModal();
  }

  #shouldShow() {
    const saved = this.#read();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    const snoozeDays = Number(this.getAttribute('snooze-days') ?? 14);
    return Date.now() - saved.at > snoozeDays * 86400000;
  }

  #read() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  #remember(state) {
    try {
      const saved = this.#read();
      if (saved?.state === 'subscribed') return;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      // Storage can be unavailable (private mode); the popup just shows again next visit.
    }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
